import { useState, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Camera,
	ChevronDown,
	Clock,
	Edit,
	Facebook,
	Globe,
	House,
	Instagram,
	MapPin,
	Monitor,
	Navigation,
	Phone,
	Sparkles,
	Star,
	Store,
} from "lucide-react";
import "./profileStaffCard.css";
import { CombinedContext } from "../../context/combinedContext";
import ShopGallery from "../common/shopGallery/shopGallery";
import StaffServicesModal from "./staffServicesModal/staffServicesModal";
import StaffInfoModal from "./staffInfoModal/staffInfoModal";
import companyBasedRequest from "../../request/companyBasedRequest";
import translations from "../../translations/translations";
import ProfileUploadModal from "../teamPage/profilePhotoModal/profilePhotoModal";
import ProfileStaffSkeleton from "./profileStaffSkeleton/profileStaffSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";

/* Height of the sticky category bar — keep in sync with staffPage.js */
const STICKY_OFFSET = 78;

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

const formatTime24To12 = (time24) => {
	if (!time24) return "";
	const [h, m] = time24.split(":").map(Number);
	const period = h >= 12 ? "PM" : "AM";
	const hour12 = h % 12 || 12;
	return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
};

const toMinutes = (time24) => {
	if (!time24) return null;
	const [h, m] = time24.split(":").map(Number);
	if (Number.isNaN(h)) return null;
	return h * 60 + (m || 0);
};

/* Live open/closed badge computed from the shop's opening hours. */
const getOpenStatus = (openingHours, copy, language) => {
	if (!Array.isArray(openingHours) || openingHours.length < 7) return null;

	const now = new Date();
	const todayIndex = (now.getDay() + 6) % 7;
	const today = openingHours[todayIndex];
	if (!today) return null;

	const closedWith = (detail) => ({ isOpen: false, word: copy.closed, detail });

	const nextOpening = () => {
		for (let step = 1; step <= 7; step += 1) {
			const day = openingHours[(todayIndex + step) % 7];
			if (!day || day.closed || !day.open) continue;
			const when = step === 1 ? copy.tomorrow : language === "es" ? day.dayES : day.dayEN;
			return `${copy.opensAt} ${when} ${formatTime24To12(day.open)}`;
		}
		return null;
	};

	if (today.closed) return closedWith(nextOpening());

	const open = toMinutes(today.open);
	const close = toMinutes(today.close);
	if (open === null || close === null) return null;

	const minutes = now.getHours() * 60 + now.getMinutes();
	const spansMidnight = close <= open;
	const isOpen = spansMidnight ? minutes >= open || minutes < close : minutes >= open && minutes < close;

	if (isOpen) return { isOpen: true, word: copy.open, detail: `${copy.until} ${formatTime24To12(today.close)}` };
	if (!spansMidnight && minutes < open) return closedWith(`${copy.opensAt} ${formatTime24To12(today.open)}`);
	return closedWith(nextOpening());
};

const formatPhone = (number) => {
	if (!number) return "";
	const digits = number.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return number.startsWith("+") ? number : `+${number}`;
};

const formatUrl = (url) => {
	if (!url) return "";
	return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
};

const TikTokIcon = ({ size = 18 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M16.6 3h-3.2v13.1a2.9 2.9 0 1 1-2.2-2.8v-3.3a6.2 6.2 0 1 0 5.4 6.1V9.4a8.6 8.6 0 0 0 4.4 1.4V7.6A5.5 5.5 0 0 1 16.6 3Z" />
	</svg>
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const ProfileStaffCard = () => {
	const { userData, language } = useContext(CombinedContext);

	const [staff, setStaff] = useState(null);
	const [staffServiceIds, setStaffServiceIds] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activeModal, setActiveModal] = useState(null);
	const [shopServices, setShopServices] = useState([]);
	const [shopData, setShopData] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);
	const [openCategories, setOpenCategories] = useState({});
	const [showFullWeek, setShowFullWeek] = useState(false);

	const categoryRefs = useRef({});
	const lockScrollSpy = useRef(0);

	const location = useLocation();
	const navigate = useNavigate();

	const t = translations[language].shopPage;
	const ts = translations[language].staffPage;
	const tp = translations[language].profile;

	useEffect(() => {
		if (!location.state?.openModal) return;
		setActiveModal(location.state.openModal);
	}, [location.state, navigate]);

	useEffect(() => {
		if (!userData?.user) return;
		setStaff({
			userId: userData.user.userId,
			firstName: userData.user.firstName,
			lastName: userData.user.lastName,
			role: userData.user.role || "",
			bio: userData.user.bio || "",
			phone: userData.user.phoneNumber || "",
			photo: userData.user.photo || null,
		});
		setStaffServiceIds(userData.user.services || []);
		setLoading(false);
	}, [userData]);

	useEffect(() => {
		if (!userData?.user?.shopId) return;
		companyBasedRequest
			.getShopServices(userData.user.shopId, userData.cognitoTokens.AccessToken)
			.then((res) => setShopServices(res.services || []))
			.catch(console.error);
	}, [userData]);

	useEffect(() => {
		if (!userData?.user?.shopId) return;
		companyBasedRequest
			.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken)
			.then((res) => setShopData(res.shop))
			.catch(console.error);
	}, [userData]);

	const displayServices = staffServiceIds.map((id) => shopServices.find((s) => s.serviceId === id)).filter(Boolean);

	const servicesByCategory = (() => {
		const cats = shopData?.serviceCategories || [];
		const grouped = cats
			.map((cat) => ({
				category: cat,
				services: displayServices.filter((s) => s.categoryId === cat.categoryId),
			}))
			.filter((g) => g.services.length > 0);

		const knownIds = new Set(cats.map((c) => c.categoryId));
		const orphans = displayServices.filter((s) => !knownIds.has(s.categoryId));
		if (orphans.length) {
			grouped.push({
				category: { categoryId: "uncategorized", name: { en: "Other", es: "Otros" } },
				services: orphans,
			});
		}
		return grouped;
	})();

	useEffect(() => {
		if (!servicesByCategory.length) return;
		setOpenCategories(Object.fromEntries(servicesByCategory.map(({ category }) => [category.categoryId, true])));
		setActiveCategory(servicesByCategory[0].category.categoryId);
	}, [shopData, shopServices, staffServiceIds]);

	/* Keep the sticky tab bar in sync with whatever category is under it. */
	useEffect(() => {
		if (!servicesByCategory.length) return undefined;
		const ids = servicesByCategory.map(({ category }) => category.categoryId);

		const onScroll = () => {
			if (Date.now() < lockScrollSpy.current) return;
			let current = ids[0];
			ids.forEach((id) => {
				const el = categoryRefs.current[id];
				if (el && el.getBoundingClientRect().top <= STICKY_OFFSET + 12) current = id;
			});
			setActiveCategory((prev) => (prev === current ? prev : current));
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [shopData, shopServices, staffServiceIds]);

	if (loading || !staff || !shopData) return <ProfileStaffSkeleton />;

	const jumpToCategory = (id) => {
		setActiveCategory(id);
		lockScrollSpy.current = Date.now() + 700;
		categoryRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const toggleCategory = (id) => setOpenCategories((prev) => ({ ...prev, [id]: prev[id] === false }));

	const fullName = [staff.firstName, staff.lastName].filter(Boolean).join(" ");
	const addressQuery = encodeURIComponent([shopData.address, shopData.city, shopData.country].filter(Boolean).join(", "));
	const owner = shopData.staffs?.find((s) => s.role === "owner");
	const reviewCount = shopData.reviews?.length || 0;
	const rating = shopData.reviewSummary?.averageRating;

	const roleLabel = ts.sidebar?.staff?.roles?.[staff.role] || null;
	const status = getOpenStatus(shopData.openingHours, t.status, language);
	const serviceCount = displayServices.length;
	const showCategoryChrome = servicesByCategory.length > 1;
	const todayIndex = (new Date().getDay() + 6) % 7;
	const todayHours = Array.isArray(shopData.openingHours) ? shopData.openingHours[todayIndex] : null;
	const phone = staff.phone || shopData.phone;
	const teammates = (shopData.staffs || []).filter((b) => b.status === "active" && b.userId !== staff.userId);

	const socials = [
		{ key: "instagram", url: shopData.socialLinks?.instagram, Icon: Instagram, label: "Instagram" },
		{ key: "facebook", url: shopData.socialLinks?.facebook, Icon: Facebook, label: "Facebook" },
		{ key: "tiktok", url: shopData.socialLinks?.tiktok, Icon: TikTokIcon, label: "TikTok" },
		{ key: "website", url: shopData.socialLinks?.website, Icon: Globe, label: t.sidebar.websiteTitle || "Website" },
	].filter((s) => s.url);

	const serviceTag = (service) => {
		const loc = service.locationType || (service.isHomeService ? "home" : "in_shop");
		if (loc === "home") {
			return (
				<span className="sf-shop-service-tag is-home">
					<House size={12} strokeWidth={2} />
					{t.main.serviceType.home}
				</span>
			);
		}
		if (loc === "online") {
			return (
				<span className="sf-shop-service-tag is-online">
					<Monitor size={12} strokeWidth={2} />
					{t.main.serviceType.online}
				</span>
			);
		}
		return null;
	};

	return (
		<div className="sf-staff-page sf-stf-page">
			<div className="sf-staff-content">
				<div className="sf-staff-main">
					<ShopGallery images={shopData.images || []} shareUrl={`https://reservasinfila.com/shops/${shopData.slug || shopData.shopId}`} />

					{/* ── IDENTITY — the person leads, the shop is context ── */}
					<header className="sf-staff-head">
						<div className="sf-staff-avatar-wrap sf-stf-avatar-wrap">
							{staff.photo ? (
								<img className="sf-staff-avatar" src={staff.photo} alt="" />
							) : (
								<span className="sf-staff-avatar is-fallback">{staff.firstName?.charAt(0)?.toUpperCase()}</span>
							)}
							<button type="button" className="sf-stf-avatar-btn" onClick={() => setActiveModal("avatar")} aria-label="Edit photo">
								<Camera size={15} strokeWidth={2} />
							</button>
						</div>

						<div className="sf-staff-head-copy">
							{roleLabel && <span className="sf-shop-eyebrow">{roleLabel}</span>}

							<div className="sf-stf-title-row">
								<h1 className="sf-shop-name">{fullName}</h1>
								<button type="button" className="sf-adm-edit" onClick={() => setActiveModal("info")} aria-label={tp.header?.edit || "Edit"}>
									<Edit size={14} />
								</button>
							</div>

							<div className="sf-shop-meta">
								{reviewCount > 0 ? (
									<span className="sf-shop-rating-pill">
										<Star size={14} strokeWidth={1.5} fill="currentColor" />
										<strong>{rating}</strong>
										<span>({t.rating.reviews.replace("{{count}}", reviewCount)})</span>
									</span>
								) : (
									<span className="sf-shop-new-pill">
										<Sparkles size={13} strokeWidth={2} />
										{tp.noReviewsyet}
									</span>
								)}

								{status && (
									<span className={`sf-shop-status ${status.isOpen ? "is-open" : "is-closed"}`}>
										<i className="sf-shop-status-dot" />
										<strong>{status.word}</strong>
										{status.detail && <span>· {status.detail}</span>}
									</span>
								)}
							</div>

							{/* the shop is context here, not the subject — a quiet link back */}
							<button type="button" className="sf-staff-shop-link" onClick={() => navigate("/profile")}>
								<Store size={15} strokeWidth={1.7} />
								<span>
									<em>{ts.at}</em>
									<strong>{shopData.name}</strong>
								</span>
							</button>

							<p className="sf-shop-address">
								<MapPin size={15} strokeWidth={1.7} />
								<span>
									{shopData.address || <span className="sf-adm-placeholder">{tp.addAddress}</span>}
									{shopData.addressDescription && <em>{shopData.addressDescription}</em>}
								</span>
							</p>
						</div>
					</header>

					{/* bio — the one piece of copy that is genuinely theirs */}
					{staff.bio ? (
						<section className="sf-shop-about">
							<p>{staff.bio}</p>
						</section>
					) : (
						<button type="button" className="sf-adm-empty sf-stf-bio-empty" onClick={() => setActiveModal("info")}>
							{language === "es" ? "Añade una biografía" : "Add a short bio"}
						</button>
					)}

					{/* ── SERVICES ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.main.services}</h2>
							{serviceCount > 0 && <span className="sf-shop-count">{serviceCount}</span>}
							<button type="button" className="sf-adm-edit sf-adm-edit--end" onClick={() => setActiveModal("services")} aria-label={tp.addServices}>
								<Edit size={14} />
							</button>
						</div>

						{serviceCount > 0 ? (
							<div className="sf-shop-services">
								{showCategoryChrome && (
									<div className="sf-shop-cat-tabs">
										{servicesByCategory.map(({ category, services }) => (
											<button
												key={category.categoryId}
												type="button"
												className={`sf-shop-cat-tab ${activeCategory === category.categoryId ? "is-active" : ""}`}
												onClick={() => jumpToCategory(category.categoryId)}
											>
												{category.name?.[language]}
												<span>{services.length}</span>
											</button>
										))}
									</div>
								)}

								{servicesByCategory.map(({ category, services }) => {
									const isOpen = !showCategoryChrome || openCategories[category.categoryId] !== false;
									return (
										<div key={category.categoryId} ref={(el) => (categoryRefs.current[category.categoryId] = el)} className="sf-shop-cat-card">
											{showCategoryChrome && (
												<button
													type="button"
													className="sf-shop-cat-header"
													onClick={() => toggleCategory(category.categoryId)}
													aria-expanded={isOpen}
												>
													<h3>{category.name?.[language]}</h3>
													<span className="sf-shop-cat-meta">
														{services.length}
														<ChevronDown size={16} className={`sf-shop-chevron ${isOpen ? "is-open" : ""}`} />
													</span>
												</button>
											)}

											<div className={`sf-shop-cat-body ${isOpen ? "is-open" : ""}`}>
												<div>
													{services.map((s) => (
														<div key={s.serviceId} className="sf-shop-service">
															<div className="sf-shop-service-copy">
																<h4 className="sf-shop-service-name">
																	{s.name}
																	{serviceTag(s)}
																</h4>
																{s.description && <p className="sf-shop-service-desc">{s.description}</p>}
																<span className="sf-shop-service-time">
																	<Clock size={13} strokeWidth={1.8} />
																	{s.duration} min
																</span>
															</div>

															<div className="sf-shop-service-action">
																<span className="sf-shop-service-price">{formatPrice(s.price, s.currency)}</span>
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<button type="button" className="sf-adm-empty" onClick={() => setActiveModal("services")}>
								{tp.addFirstServices}
							</button>
						)}
					</section>
				</div>

				{/* ── STICKY SIDEBAR ── */}
				<aside className="sf-staff-sidebar">
					<div className="sf-shop-card">
						{shopData.lat && shopData.lng && (
							<div className="sf-shop-map">
								<ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
							</div>
						)}

						<div className="sf-shop-card-body">
							<div className="sf-shop-place">
								<MapPin size={16} strokeWidth={1.7} />
								<span>
									<strong>{shopData.address}</strong>
									{(shopData.city || shopData.country) && <em>{[shopData.city, shopData.country].filter(Boolean).join(", ")}</em>}
								</span>
							</div>

							{shopData.address && (
								<a
									className="sf-shop-directions"
									href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									<Navigation size={15} strokeWidth={2} />
									{t.sidebar.getDirections}
								</a>
							)}

							<div className="sf-shop-rule" />

							<p className="sf-shop-label">{t.sidebar.contactAndHours}</p>
							<div className="sf-shop-contact">
								<a href={phone ? `tel:${phone}` : undefined} className="sf-shop-phone">
									<Phone size={15} strokeWidth={1.7} />
									{phone ? formatPhone(phone) : t.sidebar.noPhone}
								</a>
								{phone && (
									<a href={`tel:${phone}`} className="sf-shop-call">
										{t.sidebar.call}
									</a>
								)}
							</div>

							{todayHours && (
								<div className="sf-shop-hours">
									<div className="sf-shop-hours-today">
										<span className="sf-shop-hours-day">
											<Clock size={14} strokeWidth={1.8} />
											{t.sidebar.hours.today}
										</span>
										<span className="sf-shop-hours-time">
											{todayHours.closed ? t.sidebar.hours.closed : `${formatTime24To12(todayHours.open)} – ${formatTime24To12(todayHours.close)}`}
										</span>
									</div>

									<button type="button" className="sf-shop-hours-toggle" onClick={() => setShowFullWeek((v) => !v)}>
										{showFullWeek ? t.sidebar.hours.hideWeek : t.sidebar.hours.showWeek}
										<ChevronDown size={14} className={`sf-shop-chevron ${showFullWeek ? "is-open" : ""}`} />
									</button>

									<div className={`sf-shop-hours-week ${showFullWeek ? "is-open" : ""}`}>
										<div>
											{shopData.openingHours.map((dayObj, index) =>
												dayObj ? (
													<div key={index} className={`sf-shop-hours-row ${todayIndex === index ? "is-today" : ""}`}>
														<span>{language === "es" ? dayObj.dayES : dayObj.dayEN}</span>
														<span>
															{dayObj.closed ? t.sidebar.hours.closed : `${formatTime24To12(dayObj.open)} – ${formatTime24To12(dayObj.close)}`}
														</span>
													</div>
												) : null,
											)}
										</div>
									</div>
								</div>
							)}

							{owner && (
								<>
									<div className="sf-shop-rule" />
									<p className="sf-shop-label">{language === "es" ? "Propietario" : "Business owner"}</p>
									<div className="sf-stf-owner">
										{owner.photo ? (
											<img className="sf-stf-owner-avatar" src={owner.photo} alt="" />
										) : (
											<span className="sf-stf-owner-avatar is-fallback">{owner.firstName?.charAt(0).toUpperCase()}</span>
										)}
										<span className="sf-stf-owner-meta">
											<strong>
												{owner.firstName} {owner.lastName}
											</strong>
											{owner.phone && (
												<a href={`tel:${owner.phone}`} className="sf-stf-owner-phone">
													<Phone size={12} strokeWidth={1.8} />
													{formatPhone(owner.phone)}
												</a>
											)}
										</span>
									</div>
								</>
							)}

							{socials.length > 0 && (
								<>
									<div className="sf-shop-rule" />
									<p className="sf-shop-label">{t.sidebar.socialTitle}</p>
									<div className="sf-shop-socials">
										{socials.map(({ key, url, Icon, label }) => (
											<a key={key} href={formatUrl(url)} target="_blank" rel="noopener noreferrer" className="sf-shop-social" title={label}>
												<Icon size={18} strokeWidth={1.7} />
												<span>{label}</span>
											</a>
										))}
									</div>
								</>
							)}
						</div>
					</div>

					{shopData.description && (
						<div className="sf-staff-team-card">
							<p className="sf-shop-label">{t.sidebar.description}</p>
							<p className="sf-stf-side-text">{shopData.description}</p>
						</div>
					)}

					{teammates.length > 0 && (
						<div className="sf-staff-team-card">
							<p className="sf-shop-label">{ts.otherStaff}</p>
							<div className="sf-shop-team">
								{teammates.map((b) => (
									<div className="sf-shop-team-card is-static" key={b.userId || b.email}>
										{b.photo ? (
											<img className="sf-shop-team-avatar" src={b.photo} alt={b.firstName || ""} loading="lazy" />
										) : (
											<span className="sf-shop-team-avatar is-fallback">{b.firstName?.charAt(0).toUpperCase()}</span>
										)}
										<span className="sf-shop-team-name">{b.firstName}</span>
									</div>
								))}
							</div>
						</div>
					)}
				</aside>
			</div>

			{activeModal === "info" && <StaffInfoModal open onClose={() => setActiveModal(null)} staff={staff} setStaff={setStaff} />}
			{activeModal === "services" && (
				<StaffServicesModal
					isOpen
					onClose={() => setActiveModal(null)}
					shopServices={shopServices}
					staffServices={staffServiceIds}
					onSave={(ids) => setStaffServiceIds(ids)}
				/>
			)}
			{activeModal === "avatar" && (
				<ProfileUploadModal
					isOpen
					staffProfile={{ userId: staff.userId, photo: staff.photo }}
					onClose={() => setActiveModal(null)}
					onUploaded={(url) => {
						setStaff((prev) => ({ ...prev, photo: url }));
						setShopData((prev) => ({
							...prev,
							staffs: prev.staffs.map((s) => (s.userId === staff.userId ? { ...s, photo: url } : s)),
						}));
					}}
				/>
			)}
		</div>
	);
};

export default ProfileStaffCard;
